import React from 'react';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import useVoyagerStore from '../../stores/useVoyagerStore';
import type { LandingAgentTurn } from '../../stores/slices/landingAgentSlice';
import { NOT_ACCEPTED_LINE } from '../../stores/slices/acceptance';
import { frameOf, notAccepted } from '../../stores/slices/progress';
import { formatKpiValue } from '../agent-common/formatKpiValue';

/** Rows the frame shows before the final card takes its place; it is a preview, the card holds every row. */
const MOST_ROWS = 10;
const PLACEHOLDERS = 3;

function Bar({ width }: { width: string }) {
  return <Box data-testid="frame-placeholder" sx={{ height: 9, width, borderRadius: 99, bgcolor: 'var(--line, var(--surface2))',
    '@media (prefers-reduced-motion: no-preference)': { animation: 'f4pulse 1.6s ease-in-out infinite' },
    '@keyframes f4pulse': { '0%, 100%': { opacity: 1 }, '50%': { opacity: 0.55 } } }} />;
}

const cell = (value: unknown, metric: boolean) => (metric ? formatKpiValue(value)
  : value === null || value === undefined ? '—' : String(value));

/**
 * F4 (#1858 §4): the answer's shape before its numbers — one query part only. With a grouping, a table whose heads are
 * the reading's labels and three placeholder rows (bars, never digits; always three); with none, a tile with the
 * metric's label and no value. Rows fill only once code accepted the part (`part_accepted {accepted: true}`), each
 * value through the card's own formatter, in the warehouse's order. The final card takes its place.
 */
export default function AnswerFrame({ turn }: { turn: LandingAgentTurn }) {
  // the final card replaces the frame once its own table can be drawn (the answer's ladder)
  if (turn.result?.ladder || turn.document) return null;
  const frame = frameOf(turn);
  if (!frame) return null;
  const grouped = frame.heads.some((h) => !h.metric);
  if (!grouped) {
    const metric = frame.heads.find((h) => h.metric)!;
    const value = frame.rows?.[0]?.[metric.key];
    return (
      <Box data-testid="answer-frame" data-shape="tile" sx={{ mb: 0.5, color: 'var(--ink)' }}>
        <Box component="span" sx={{ display: 'block', fontSize: 12, color: 'var(--ink-3)' }}>{metric.label}</Box>
        {frame.rows ? (
          <Box className="stroma-number" data-testid="frame-value"
               sx={{ fontFamily: 'var(--display)', fontWeight: 700, fontSize: 26, lineHeight: 1.2 }}>{cell(value, true)}</Box>
        ) : <Box sx={{ pt: 1.25, pb: 0.75 }}><Bar width="96px" /></Box>}
      </Box>
    );
  }
  const shown = frame.rows ? frame.rows.slice(0, MOST_ROWS) : null;
  return (
    <Box data-testid="answer-frame" data-shape="table" sx={{ mb: 0.5, overflowX: 'auto', color: 'var(--ink)',
      '& table': { borderCollapse: 'collapse', fontSize: 14, minWidth: 260 },
      '& th': { p: '6px 18px 6px 0', borderBottom: '1px solid var(--line)', fontWeight: 600, color: 'var(--ink-2)', whiteSpace: 'nowrap' },
      '& td': { p: '6px 18px 6px 0', borderBottom: '1px solid var(--sunk)', whiteSpace: 'nowrap' },
      '& td.f4-wait': { py: '9px' }, '& .f4-num': { textAlign: 'right' }, '& .f4-more': { color: 'var(--ink-3)' } }}>
      <table>
        <thead>
          <tr>
            {frame.heads.map((h) => (
              <th key={h.key} className={h.metric ? 'f4-num' : undefined} style={{ textAlign: h.metric ? 'right' : 'left' }}>{h.label}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {shown ? shown.map((row, r) => (
            <tr key={r} data-testid="frame-row">
              {frame.heads.map((h) => (
                <td key={h.key} className={h.metric ? 'stroma-number f4-num' : undefined}>{cell(row[h.key], h.metric)}</td>
              ))}
            </tr>
          )) : Array.from({ length: PLACEHOLDERS }, (_, r) => (
            <tr key={r}>
              {frame.heads.map((h, k) => (
                <td key={h.key} className="f4-wait">
                  <Box sx={{ display: 'flex', justifyContent: h.metric ? 'flex-end' : 'flex-start' }}>
                    <Bar width={h.metric ? '56px' : `${[88, 64, 76][(r + k) % 3]}px`} />
                  </Box>
                </td>
              ))}
            </tr>
          ))}
          {shown && frame.rows!.length > shown.length && (
            <tr><td colSpan={frame.heads.length} className="f4-more">…</td></tr>
          )}
        </tbody>
      </table>
    </Box>
  );
}

/**
 * F4 (#1858 §4a): a part code did not accept is not answered — the server's own line in place of its table, with Try
 * again (the turn's own request, asked again). When the whole answer is that line (its headline already says it), only
 * Try again is added.
 */
export function NotAcceptedLine({ turn }: { turn: LandingAgentTurn }) {
  const send = useVoyagerStore((s) => s.sendLandingAgentQuery);
  const parts = notAccepted(turn);
  if (!parts.length) return null;
  const reask = turn.askedRequest;
  const said = turn.result?.synthesis?.headline === NOT_ACCEPTED_LINE;
  return (
    <Box data-testid="not-accepted" sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap', minWidth: 0 }}>
      {!said && <Box component="span" sx={{ fontSize: 13.5, color: 'var(--ink-2)' }}>{NOT_ACCEPTED_LINE}</Box>}
      {reask && turn.status !== 'running' && <Button size="small" variant="outlined" data-testid="not-accepted-retry"
                        onClick={() => send(reask.query, { reask })} sx={{ textTransform: 'none' }}>Try again</Button>}
    </Box>
  );
}
